import React, { useState } from "react";
import { connect } from "react-redux";
import { get, isEmpty } from "lodash";

import { NavLink } from "react-router-dom";
import {
  EnvironmentOutlined,
  HomeOutlined,
  SolutionOutlined,
  FireOutlined,
  SwapOutlined,
  TeamOutlined,
  DollarCircleOutlined,
  FileDoneOutlined,
  LineChartOutlined,
  BellOutlined,
  TagsOutlined,
} from "@ant-design/icons";

import { DEFAULT_PAGE_SIZE } from "../../common/navs";

import { Menu } from "antd";

import styles from "./Nav.module.scss";
import {
  getStoredPageSettings,
  updateStoredPageSettings,
} from "../../utils/settings";
import { hasPermission, PERMISSIONS } from "../../common/permissions";

const mapStateToProps = (state) => ({
  entity: get(state.entity, "data", {}),
  user: get(state.user, "data", {}),
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

const REPORTS_USER_IDS = [4, 5, 18];
const ALL_SECTION_KEYS = ["g1", "g2", "g3", "g5", "g6"];
const NAV_STORAGE_KEY = "nav.management";

const Management = (props) => {
  const { collapsed, navSelectedKeys, entity, user } = props;
  const canViewRevenue = hasPermission(
    user,
    PERMISSIONS.MANAGEMENT_REVENUE_READ
  );

  const [openSections, setOpenSections] = useState(() => {
    const settings = getStoredPageSettings(NAV_STORAGE_KEY);
    return settings.openSections || ALL_SECTION_KEYS;
  });

  const handleOpenChange = (newSections) => {
    if (collapsed) return;
    setOpenSections(newSections);
    updateStoredPageSettings(NAV_STORAGE_KEY, { openSections: newSections });
  };

  let filters = "";
  if (!isEmpty(entity)) {
    filters = `filters=entity_id%3D${entity.id}`;
  }

  const getMenuItems = () => {
    return [
      // Management Group
      {
        key: "g1",
        label: "Management",
        children: [
          {
            key: "entities",
            label: (
              <NavLink
                to={`/management/entities?limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <HomeOutlined />
                <span>Entities</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "deals",
            label: (
              <NavLink
                to={`/management/deals?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <SolutionOutlined />
                <span>Deals</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "assets",
            label: (
              <NavLink
                to={`/management/assets?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <EnvironmentOutlined />
                <span>Assets</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "tracts",
            label: (
              <NavLink
                to={`/management/tracts?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <EnvironmentOutlined />
                <span>Tracts</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "operator_payors",
            label: (
              <NavLink
                to={`/management/operator-payors?limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <TeamOutlined />
                <span>Operator payors</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "operators",
            label: (
              <NavLink
                to={`/management/operators?limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <TeamOutlined />
                <span>Operators</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "management-wells",
            label: (
              <NavLink
                to={`/management/wells?limit=${DEFAULT_PAGE_SIZE}&page=1&sort=-id`}
              >
                <FireOutlined />
                <span>Wells</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "division_orders",
            label: (
              <NavLink
                to={`/management/division-orders?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <FileDoneOutlined />
                <span>Division Orders</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
        ],
      },
      // Revenue Group (gated on management.revenue.read)
      ...(canViewRevenue
        ? [
            {
              key: "g2",
              label: "Revenue",
              children: [
                {
                  key: "royalties",
                  label: (
                    <NavLink
                      to={`/management/royalties?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
                    >
                      <DollarCircleOutlined />
                      <span>Royalties</span>
                    </NavLink>
                  ),
                  className: styles.siderMenuItem,
                },
                {
                  key: "sales",
                  label: (
                    <NavLink
                      to={`/management/sales?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
                    >
                      <DollarCircleOutlined />
                      <span>Sales</span>
                    </NavLink>
                  ),
                  className: styles.siderMenuItem,
                },
              ],
            },
          ]
        : []),
      // Taxes Group
      {
        key: "g3",
        label: "Tax",
        children: [
          {
            key: "taxing_entities",
            label: (
              <NavLink
                to={`/management/taxing-entities?limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <HomeOutlined />
                <span>Entities</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "tax_statements",
            label: (
              <NavLink
                to={`/management/tax-statements?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <DollarCircleOutlined />
                <span>Statements</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "tax_appraisals",
            label: (
              <NavLink
                to={`/management/tax-appraisals?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <DollarCircleOutlined />
                <span>Appraisals</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
        ],
      },
      // Ops Group
      {
        key: "g5",
        label: "Ops",
        children: [
          {
            key: "tickets",
            label: (
              <NavLink
                to={`/management/tickets?${
                  filters ? `${filters}%26done%3Dfalse` : "filters=done%3Dfalse"
                }&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <TagsOutlined />
                <span>Tickets</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
          {
            key: "alerts",
            label: (
              <NavLink
                to={`/management/alerts?filters=status%3Dpending&limit=${DEFAULT_PAGE_SIZE}&page=1`}
              >
                <BellOutlined />
                <span>Alerts</span>
              </NavLink>
            ),
            className: styles.siderMenuItem,
          },
        ],
      },
      // Reports Group (restricted to specific users)
      ...(REPORTS_USER_IDS.includes(user.id)
        ? [
            {
              key: "g6",
              label: "Reports",
              children: [
                {
                  key: "main",
                  label: (
                    <NavLink
                      to={`/management/reports/main?${filters}&limit=${DEFAULT_PAGE_SIZE}&page=1`}
                    >
                      <LineChartOutlined />
                      <span>Main</span>
                    </NavLink>
                  ),
                  className: styles.siderMenuItem,
                },
              ],
            },
          ]
        : []),
    ];
  };

  return (
    <Menu
      theme="light"
      mode="inline"
      className={collapsed ? null : styles.siderMenu}
      selectedKeys={navSelectedKeys}
      openKeys={collapsed ? [] : openSections}
      onOpenChange={handleOpenChange}
      items={getMenuItems()}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Management);
